import type { ReactNode } from "react";
import clsx from "clsx";
import { useT } from "../i18n";
import { TonePill } from "../status/StatusBadge";
import { CopyButton } from "./CopyButton";

/**
 * Error that needs a person (§3): what happened / what to do / details (mono) / reference id, then actions.
 * Copy follows §7: cause + next step, never blaming the user.
 */
export function ErrorPanel({ title, what, todo, details, refId, when, badge = "Failed", actions, className }: {
  title: ReactNode; what: ReactNode; todo: ReactNode; details?: string; refId?: string; when?: ReactNode; badge?: ReactNode; actions?: ReactNode; className?: string;
}) {
  const t = useT();
  const detailText = [details, refId ? `ref ${refId}` : null].filter(Boolean).join(" · ");
  return (
    <section className={clsx("iga-card flex flex-col gap-3.5 p-5", className)} role="alert" aria-labelledby={undefined}>
      <div className="flex flex-wrap items-center gap-3">
        <TonePill tone="bad">{badge}</TonePill>
        {when && <span className="text-small text-fg-3">{when}</span>}
      </div>
      <h2 className="text-[17px] font-semibold leading-6">{title}</h2>
      <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[120px_minmax(0,1fr)]">
        <dt className="text-fg-3">{t("ui.whatHappened")}</dt><dd>{what}</dd>
        <dt className="text-fg-3">{t("ui.whatToDo")}</dt><dd>{todo}</dd>
        {detailText && <><dt className="text-fg-3">{t("ui.details")}</dt><dd className="break-words font-mono text-[12.5px] text-fg-2">{detailText}</dd></>}
      </dl>
      {(actions || detailText) && (
        <div className="flex flex-wrap items-center gap-2.5">
          {actions}
          {detailText && <CopyButton value={detailText} label={t("ui.copyDetails")} variant="ghost" />}
        </div>
      )}
    </section>
  );
}
